import { pick } from 'es-toolkit';
import React, { useEffect, useState } from 'react';
import { produce } from 'immer';
import { Vector3 } from 'three';
import { create } from 'zustand';

import { TrussEnvelope } from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';
import { Writable } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../../library/layout-geometry/coordinates';
import { LayoutCoordinateVisuals } from '../../../library/layout-geometry/LayoutCoordinateVisuals';
import { TrussEnvelopeHistory } from '../../../library/layout-geometry/truss-envelopes/calculation/envelopeCalculationResult';
import { TrussEnvelopeInsertionInformation } from '../../../library/layout-geometry/truss-envelopes/insertion-information';
import { TrussEnvelopeSnappingSettings } from '../../../library/layout-geometry/truss-envelopes/types';
import { insertAndRepeatTrussEnvelopeIfFullyDefined } from '../../../store/actions/layout/trussEnvelopeActions';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { getPickingObjectInfo } from '../../../store/selectors/pickingSelectors';
import { getTrussEnvelopeInsertionInformation } from '../../../store/selectors/trussEnvelopeInsertionInformationSelectors';
import { IPickingObjectInfo, setInsertionTrussEnvelopeProperties } from '../../../store/slices/applicationSlice';
import { QuickDistanceDimension } from '../../dimension/QuickDistanceDimension';
import { threeJsDom } from '../../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../../util/events/types';
import { ImportantPoint } from '../FiberDevTools';
import { TrussEnvelopeVisual } from './TrussEnvelopeVisual';

export const INSERTION_ENVELOPE_PROPS = {
  style: 'emphasised' as const,
  showEndLabels: true,
  showTrussSolid: false,
  renderMode: 'envelope' as const,
  elevation: 0,
};

export const useActualTrussEnvelopeSnappingSettings = create<{
  settingsByEnvelope: Record<string, TrussEnvelopeSnappingSettings>;
  setActualSettings: (guid: string, settings: TrussEnvelopeSnappingSettings | null) => void;
}>((set) => ({
  settingsByEnvelope: {},

  setActualSettings(guid, settings) {
    set(
      produce((state) => {
        if (settings == null) {
          delete state.settingsByEnvelope[guid];
        } else {
          state.settingsByEnvelope[guid] = settings;
        }
      }),
    );
  },
}));

export function TrussEnvelopeInsertionVisual() {
  const in3D = useAppSelector(is3DLayoutCameraInUse);
  const insertion = useAppSelector(getTrussEnvelopeInsertionInformation);

  if (insertion == null) return null;

  return insertion.isInsertionEnvelopeFullyDefined() ? (
    <TrussEnvelopeVisual inert in3D={in3D} {...INSERTION_ENVELOPE_PROPS} envelope={{ name: '-1', ...insertion.data }} />
  ) : (
    <TrussEnvelopePreview insertion={insertion} />
  );
}

// Normally we want to disable the insertion visual whenever picking is occurring to prevent it from jumping all over
//   the screen as the user's mouse moves around in search of the object they are picking.
function shouldPickingBlockInsertion(pickingObjectInfo: IPickingObjectInfo | null) {
  if (pickingObjectInfo == null) return false;

  // Special case -- when picking a point for an insertion envelope we still want the insertion visual to be visible,
  //   as the chosen point will alter the envelope.
  if (pickingObjectInfo?.type === 'pickingLayoutPoint' && pickingObjectInfo.target.startsWith('insertion-envelope')) {
    return false;
  }

  return true;
}

function TrussEnvelopePreview(props: { insertion: TrussEnvelopeInsertionInformation }) {
  const dispatch = useAppDispatch();

  const in3D = useAppSelector(is3DLayoutCameraInUse);
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  const [mousePosition, setMousePosition] = useState<Point2D | null>(null);
  const recordActualSettings = useActualTrussEnvelopeSnappingSettings((state) => state.setActualSettings);

  useEffect(() => {
    function onClick(event: GlobalThreeJSDomObjectEvent) {
      if (shouldPickingBlockInsertion(pickingObjectInfo)) return;

      const inParallelLinePreviewMode = !(
        pickingObjectInfo?.type === 'pickingLayoutPoint' && pickingObjectInfo.target.startsWith('insertion-envelope')
      );

      if (inParallelLinePreviewMode) {
        const mousePosition = convertFromDesignToLayoutCoordinates(event.eventPosition.worldPosition).projectTo2D();
        const calculationResult = props.insertion.calculateInsertionEnvelopeUsingMousePositionIfPossible(mousePosition);

        if (calculationResult == null) {
          // Not an error -- the click likely just occurred in a position where the roof solid can't be sliced.
          return;
        }

        type Result = Writable<Partial<Omit<TrussEnvelope, 'guid'>>>;
        const result: Result = pick(calculationResult, [
          'leftPoint',
          'rightPoint',
          'elevation',
          'leftBevelCut',
          'rightBevelCut',
          'leftOverhang',
          'rightOverhang',
        ]);

        dispatch(setInsertionTrussEnvelopeProperties(result));

        if (event?.metadata.altKey) {
          dispatch(insertAndRepeatTrussEnvelopeIfFullyDefined());
        }

        return false;
      }
    }

    function onMouseMove(event: GlobalThreeJSDomObjectEvent) {
      const mousePosition = convertFromDesignToLayoutCoordinates(event.eventPosition.worldPosition).projectTo2D();
      setMousePosition(mousePosition);

      if (props.insertion.isFeaturePatternContainerInferenceActive()) {
        let inferedContainerGuid = null;

        const featurePatternContainers = props.insertion.ecosystem.getFeaturePatternContainerDictionaryIn2D();
        for (const [containerGuid, containerPolygon] of Object.entries(featurePatternContainers)) {
          if (containerPolygon?.containsPoint(mousePosition)) {
            inferedContainerGuid = containerGuid;
            break;
          }
        }

        if (inferedContainerGuid !== props.insertion.data.featurePatternContainerGuid) {
          dispatch(setInsertionTrussEnvelopeProperties({ featurePatternContainerGuid: inferedContainerGuid }));
        }
      }
    }

    function onMouseLeave() {
      setMousePosition(null);
      recordActualSettings(props.insertion.data.guid, null);
    }

    threeJsDom.addGlobalListener('click', onClick);
    threeJsDom.addGlobalListener('mousemove', onMouseMove);
    threeJsDom.addGlobalListener('mouseleave', onMouseLeave);

    return () => {
      threeJsDom.removeGlobalListener('click', onClick);
      threeJsDom.removeGlobalListener('mousemove', onMouseMove);
      threeJsDom.removeGlobalListener('mouseleave', onMouseLeave);
    };
  });

  if (shouldPickingBlockInsertion(pickingObjectInfo)) return;

  // This visual is used when the insertion envelope is not yet fully defined, so we need to predict where the envelope
  //   will be created if a point is selected at the current mouse position.
  const calculationResult = mousePosition
    ? props.insertion.calculateInsertionEnvelopeUsingMousePositionIfPossible(mousePosition)
    : null;

  if (calculationResult == null) {
    return null;
  }

  if (calculationResult.history) {
    if (calculationResult.history.stageTwo.source.origin.type === 'parallel-line') {
      recordActualSettings(props.insertion.data.guid, calculationResult.history.stageTwo.source.origin.actualSettings);
    }
  }

  const hypotheticalEnvelope = calculationResult.toTrussEnvelope('0', props.insertion.data);

  return (
    <group>
      <InsertionEnvelopeDecorations history={calculationResult.history} />
      <TrussEnvelopeVisual inert in3D={in3D} {...INSERTION_ENVELOPE_PROPS} envelope={hypotheticalEnvelope} />
    </group>
  );
}

function toVector(point: Point2D) {
  return new Vector3(...point.to3D().toArray());
}

function InsertionEnvelopeDecorations(props: { history: TrussEnvelopeHistory | undefined }) {
  if (props.history == null) return null;
  if (props.history.stageOne.origin.type !== 'parallel-line') return null;

  const {
    line,
    definingPoint,
    origin: { snappingTarget },
  } = props.history.stageOne;

  const pointOnLine = definingPoint.projectOntoLine(line);
  const projectedPoint = snappingTarget.getProjectedPoint(definingPoint)!;

  return (
    <LayoutCoordinateVisuals>
      <ImportantPoint radius={1} position={toVector(pointOnLine)} />
      <ImportantPoint radius={1} position={toVector(projectedPoint)} />

      <QuickDistanceDimension
        start={pointOnLine}
        end={projectedPoint}
        distance={pointOnLine.distanceTo(projectedPoint)}
        showEndpointMarks
      />
    </LayoutCoordinateVisuals>
  );
}
